import { useState } from "react";
import { useSearchParams } from "react-router";
import ChevronDown from "src/design-system/icons/ChevronDown.svg?react";
import ChevronUp from "src/design-system/icons/ChevronUp.svg?react";
import {
  Button,
  Checkbox,
  TextField,
  Input,
  Label,
} from "react-aria-components";
import { useFeatureFlagConfig } from "src/feature/useFeatureFlag";
import { makeBackendFeatureFlag } from "src/utils/hooks/useBackendFeatures";
import Check from "src/design-system/icons/Check.svg?react";
import { useQueryClient } from "@tanstack/react-query";

export function BackendFeatureSelector() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [isExpanded, setIsExpanded] = useState(false);

  function toggleExpand() {
    if (isExpanded) setIsExpanded(false);
    else setIsExpanded(true);
  }

  const featureFlags = useFeatureFlagConfig();
  const [filter, setFilter] = useState("");

  // Filter feature flags by the filter input
  const filteredFeatureFlags = featureFlags
    ? Object.entries(featureFlags).filter(([featureName]) =>
        featureName.toLowerCase().includes(filter.toLowerCase()),
      )
    : [];

  return (
    <>
      <Button
        className="flex w-full cursor-pointer items-center justify-between p-4 text-left text-sm 2xl:w-100"
        onPress={toggleExpand}
      >
        <h3>Backend Feature Flags</h3>
        {!isExpanded ? <ChevronDown /> : <ChevronUp />}
      </Button>

      {isExpanded && (
        <>
          <div className="flex justify-end px-4 py-2">
            <TextField className="w-full">
              <Label className="sr-only">Filter feature flags</Label>
              <Input
                type="text"
                className="w-full rounded border border-input-border bg-white px-2 py-1 text-sm text-black placeholder:text-gray-400 focus:ring-2 focus:outline-none"
                placeholder="Filter features..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
            </TextField>
          </div>
          {filteredFeatureFlags
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([featureName, value]) => {
              function onCheckboxChange(isSelected: boolean) {
                searchParams.set(
                  "backendFeatures",
                  makeBackendFeatureFlag(featureName, isSelected),
                );
                setSearchParams(searchParams);
                queryClient.clear();
              }

              return (
                <Checkbox
                  className="flex w-full cursor-pointer items-center justify-between p-4 text-left text-sm data-hovered:bg-gray-100 data-pressed:bg-gray-200 2xl:w-100"
                  key={featureName}
                  value={value ? "true" : "false"}
                  isSelected={value}
                  defaultSelected={value}
                  onChange={onCheckboxChange}
                >
                  {({ isSelected }) => (
                    <div className="flex w-full justify-between">
                      <span className="flex-grow-3">{featureName}</span>
                      <div className="flex h-4.5 w-4.5 items-center justify-center border-1 border-input-border">
                        {isSelected && <Check className="h-3 w-3" />}
                      </div>
                    </div>
                  )}
                </Checkbox>
              );
            })}
        </>
      )}
    </>
  );
}
